import { type ReactNode, type CSSProperties } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { useVenue, initialFor } from "../lib/venue";
import { useAuth } from "../lib/auth";
import { overlayColors, overlayScrimVertical } from "../lib/image-overlay";
import { pushCtaClick } from "../lib/analytics";
import { SiteFooter } from "./site-footer";
import { MailingListPopup } from "./mailing-list-popup";

// Generic white-label WEBSITE chrome for any archetype that opts in
// (appConfig.websiteMode). Browser visitors get a real website, sticky header,
// a proper hero, document-flow content, and the white-label footer, while the
// NATIVE app keeps the app-shell experience. Themed entirely from the venue's
// own branding tokens.
export function VenueWebLayout({ children }: { children: ReactNode }) {
  const { data } = useVenue();
  const venue = data?.venue;
  const venueName = venue?.appName || venue?.name || "";
  const logoSrc = venue?.appIconUrl || venue?.imageUrl || null;
  const bannerSrc = venue?.bannerUrl || venue?.imageUrl || null;
  const welcome = venue?.appWelcomeText || venue?.appDescription || "";
  const { user } = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Admin pages escape the centred site container (full-width admin chrome).
  const vwIsAdmin = pathname === "/admin" || pathname.startsWith("/admin/");
  const isHome = pathname === "/";

  // DESIGN CONTROLS, the same appConfig-driven system the app uses (Branding
  // admin), applied to the website hero so the site is designable per venue:
  // heading treatment (size/weight/uppercase + numeric overrides) and image
  // overlay (scrim + text colours over the banner).
  const hc = venue?.appConfig?.heading;
  const heroHeadingClass = [
    hc?.sizePx != null ? "" : hc?.size === "md" ? "text-3xl sm:text-4xl" : hc?.size === "lg" ? "text-4xl sm:text-5xl" : "text-4xl sm:text-6xl",
    hc?.weightValue != null ? "" : hc?.weight === "normal" ? "font-normal" : hc?.weight === "black" ? "font-black" : "font-bold",
    hc?.uppercase ? "uppercase tracking-tight" : "tracking-tight",
    "max-w-3xl leading-[1.05]",
  ].filter(Boolean).join(" ");
  const heroHeadingStyle: CSSProperties = {
    ...(hc?.sizePx != null ? { fontSize: hc.sizePx } : {}),
    ...(hc?.weightValue != null ? { fontWeight: hc.weightValue } : {}),
    textWrap: "balance" as never,
  };
  const ov = overlayColors(venue?.appConfig?.imageOverlay);

  return (
    <div className="flex min-h-[100dvh] flex-col bg-[var(--background)]">
      {/* Top nav */}
      <header className="sticky top-0 z-30 border-b border-[var(--border-default)] bg-[var(--background)]/95 backdrop-blur">
        <div className="mx-auto flex w-full max-w-6xl items-center gap-4 px-4 py-3">
          <Link to="/" className="flex items-center gap-2 font-bold text-[var(--foreground)]">
            {logoSrc ? (
              <img src={logoSrc} alt={venueName} className="h-8 w-8 rounded-full object-cover" />
            ) : (
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--accent)] text-sm" style={{ color: "var(--accent-fg)" }}>
                {initialFor(venueName)}
              </span>
            )}
            <span className="hidden sm:inline">{venueName}</span>
          </Link>
          <nav className="ml-auto flex items-center gap-5 text-sm">
            <Link to="/events" className="hidden text-[var(--subtle)] transition hover:text-[var(--foreground)] sm:inline">
              Events
            </Link>
            {venue?.appConfig?.shopUrl && (
              <a
                href={venue.appConfig.shopUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--subtle)] transition hover:text-[var(--foreground)]"
              >
                Shop
              </a>
            )}
            <Link to={user ? "/account" : "/sign-in"} className="text-[var(--subtle)] transition hover:text-[var(--foreground)]">
              {user ? "Account" : "Sign in"}
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero (home only), banner, name, welcome, CTAs */}
      {isHome && (
        <section
          className="relative flex min-h-[62vh] items-end overflow-hidden"
          style={{
            backgroundColor: "var(--accent)",
            backgroundImage: bannerSrc ? `url("${bannerSrc}")` : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          {/* Legibility scrim over the banner, admin-controlled via the same
              image-overlay design control the app uses (text light/dark + opacity). */}
          {bannerSrc && (
            <div className="absolute inset-0" style={{ background: overlayScrimVertical(ov) }} aria-hidden />
          )}
          <div className="relative mx-auto w-full max-w-6xl px-4 py-12 sm:py-16">
            <h1
              className={heroHeadingClass}
              style={{ ...heroHeadingStyle, color: bannerSrc ? ov.fg : "var(--accent-fg)" }}
            >
              {venueName}
            </h1>
            {welcome && (
              <p className="mt-4 max-w-xl text-base sm:text-lg" style={{ color: bannerSrc ? ov.sub : "color-mix(in srgb, var(--accent-fg) 88%, transparent)" }}>
                {welcome}
              </p>
            )}
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/events"
                className="rounded-full px-5 py-2.5 text-sm font-semibold shadow-sm transition hover:opacity-90"
                style={{ backgroundColor: "var(--background)", color: "var(--foreground)" }}
              >
                What's on
              </Link>
              <a
                href="#site-footer"
                onClick={() => pushCtaClick("subscribe")}
                className="rounded-full border px-5 py-2.5 text-sm font-semibold transition hover:opacity-90"
                style={{ borderColor: bannerSrc ? ov.sub : "var(--accent-fg)", color: bannerSrc ? ov.fg : "var(--accent-fg)" }}
              >
                Join the list
              </a>
            </div>
          </div>
        </section>
      )}

      {/* Content (document flow, comfortable web width) */}
      <main className={vwIsAdmin ? "w-full flex-1 px-4 py-6 lg:px-0" : "mx-auto w-full max-w-5xl flex-1 px-4 py-10"}>{children}</main>

      {/* White-label footer (addresses, links, socials, mailing list, copyright) */}
      <div id="site-footer">
        <SiteFooter />
      </div>

      {/* Email-capture pop-up, web only (guarded inside the component too). */}
      <MailingListPopup />
    </div>
  );
}
